<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace, push } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { api, ApiError } from '../lib/api';
  import { formatDateTime } from '../lib/format';
  import DatePicker from '../components/DatePicker.svelte';
  import UtmFilter from '../components/UtmFilter.svelte';
  import { adminState } from '../lib/admin-state';
  import type { AdminPaymentDTO, AdminProductDTO, PaymentStatus, UtmSelection } from '../lib/types';
  import type { DateOp } from '../lib/admin-state';

  /** Sentinel in the source filter meaning "no source" — mirrors the API. */
  const SOURCE_NONE = '__none__';

  let payments = $state<AdminPaymentDTO[] | null>(null);
  let products = $state<AdminProductDTO[]>([]);
  let total = $state(0);
  let sumCents = $state(0);
  let page = $state(1);
  let hasMore = $state(false);
  let loading = $state(false);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);

  let search = $state(adminState.paymentsSearch);
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let backfilling = $state(false);
  let backfillMsg = $state<string | null>(null);

  // Applied filters (drive the query). Seeded from the module-level adminState
  // cache and written back below, so a drill-in into a payment and «Назад»
  // preserves the filters instead of resetting them.
  let statusFilter = $state<PaymentStatus[]>(adminState.paymentsStatusFilter);
  let productFilter = $state<string>(adminState.paymentsProductFilter); // '' = all, 'unmatched', or productId
  let sourceFilter = $state<string[]>(adminState.paymentsSourceFilter); // values + SOURCE_NONE
  let utmFilter = $state<UtmSelection>(adminState.paymentsUtmFilter);
  let dateOp = $state<'' | DateOp>(adminState.paymentsDateOp);
  let date1 = $state(adminState.paymentsDate1);
  let date2 = $state(adminState.paymentsDate2);

  // Persist every filter change back to the cache.
  $effect(() => {
    adminState.paymentsSearch = search;
    adminState.paymentsStatusFilter = statusFilter;
    adminState.paymentsProductFilter = productFilter;
    adminState.paymentsSourceFilter = sourceFilter;
    adminState.paymentsUtmFilter = utmFilter;
    adminState.paymentsDateOp = dateOp;
    adminState.paymentsDate1 = date1;
    adminState.paymentsDate2 = date2;
  });

  // Source options for the modal (distinct across all payments).
  let availableSources = $state<{ source: string; count: number }[] | null>(null);
  let sourcesHasNone = $state(false);

  // Filter modal + its draft state (committed on «Застосувати»).
  let filterOpen = $state(false);
  let draftStatuses = $state<PaymentStatus[]>([]);
  let draftProduct = $state('');
  let draftSources = $state<string[]>([]);
  let draftUtm = $state<UtmSelection>({});
  let draftDateOp = $state<'' | DateOp>('');
  let draftDate1 = $state('');
  let draftDate2 = $state('');

  /** Скільки значень обрано в UTM-виборі загалом (по всіх вимірах). */
  function utmCount(sel: UtmSelection): number {
    return Object.values(sel).reduce((n, vals) => n + (vals?.length ?? 0), 0);
  }

  const activeFiltersCount = $derived(
    statusFilter.length +
      (productFilter ? 1 : 0) +
      sourceFilter.length +
      utmCount(utmFilter) +
      (dateOp ? 1 : 0),
  );

  const STATUS_LABEL: Record<PaymentStatus, string> = {
    succeeded: 'Успішно',
    refunded: 'Повернення',
    failed: 'Відхилено',
    pending: 'Очікує',
  };
  const STATUS_CLASS: Record<PaymentStatus, string> = {
    succeeded: 'badge-active',
    refunded: 'badge-expired',
    failed: 'badge-expired',
    pending: 'badge-muted',
  };

  function uah(cents: number): string {
    return `₴${(cents / 100).toLocaleString('uk-UA', { maximumFractionDigits: 2 })}`;
  }

  /**
   * Serialises a picked date value for the API. For «дорівнює» (on) the input
   * is a plain calendar day (YYYY-MM-DD) — sent as-is (backend matches the whole
   * UTC day). For «до/після/між» the input is a local datetime-local value —
   * converted to a full ISO instant so the server compares the exact moment.
   */
  function dateParam(op: '' | DateOp, val: string): string | undefined {
    if (!op || !val) return undefined;
    if (op === 'on') return val;
    // before/after/between carry a local datetime ('YYYY-MM-DDTHH:mm') → ISO instant.
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  }

  function queryOpts(p: number) {
    return {
      search: search.trim() || undefined,
      status: statusFilter.length ? statusFilter : undefined,
      productId: productFilter && productFilter !== 'unmatched' ? productFilter : undefined,
      unmatchedOnly: productFilter === 'unmatched' || undefined,
      source: sourceFilter.length ? sourceFilter : undefined,
      utm: utmCount(utmFilter) ? utmFilter : undefined,
      dateOp: dateOp || undefined,
      date1: dateParam(dateOp, date1),
      date2: dateOp === 'between' ? dateParam(dateOp, date2) : undefined,
      page: p,
      limit: 30,
    };
  }

  async function load(): Promise<void> {
    loading = true;
    error = null;
    try {
      const res = await api.adminPayments(queryOpts(1));
      payments = res.items;
      total = res.total;
      sumCents = res.sumCents;
      page = res.page;
      hasMore = res.hasMore;
    } catch (err) {
      error = err instanceof ApiError ? `Помилка (${err.code})` : 'Не вдалось завантажити';
    } finally {
      loading = false;
    }
  }

  async function loadMore(): Promise<void> {
    if (loadingMore || !hasMore) return;
    loadingMore = true;
    try {
      const res = await api.adminPayments(queryOpts(page + 1));
      payments = [...(payments ?? []), ...res.items];
      page = res.page;
      hasMore = res.hasMore;
    } catch {
      /* keep current list on error */
    } finally {
      loadingMore = false;
    }
  }

  function onSearchInput(): void {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(load, 350);
  }

  function openFilterModal(): void {
    draftStatuses = [...statusFilter];
    draftProduct = productFilter;
    draftSources = [...sourceFilter];
    draftUtm = { ...utmFilter };
    draftDateOp = dateOp;
    draftDate1 = date1;
    draftDate2 = date2;
    filterOpen = true;
    if (availableSources === null) {
      api
        .adminPaymentSources()
        .then((r) => {
          availableSources = r.items;
          sourcesHasNone = r.hasNone;
        })
        .catch(() => {
          if (availableSources === null) availableSources = [];
        });
    }
  }
  function closeFilterModal(): void {
    filterOpen = false;
  }
  function toggleDraftStatus(s: PaymentStatus): void {
    draftStatuses = draftStatuses.includes(s)
      ? draftStatuses.filter((x) => x !== s)
      : [...draftStatuses, s];
  }
  function toggleDraftSource(s: string): void {
    draftSources = draftSources.includes(s)
      ? draftSources.filter((x) => x !== s)
      : [...draftSources, s];
  }
  const draftHasAny = $derived(
    draftStatuses.length > 0 ||
      !!draftProduct ||
      draftSources.length > 0 ||
      utmCount(draftUtm) > 0 ||
      !!draftDateOp,
  );
  function resetDraftFilters(): void {
    draftStatuses = [];
    draftProduct = '';
    draftSources = [];
    draftUtm = {};
    draftDateOp = '';
    draftDate1 = '';
    draftDate2 = '';
  }
  function applyFilters(): void {
    statusFilter = [...draftStatuses];
    productFilter = draftProduct;
    sourceFilter = [...draftSources];
    utmFilter = { ...draftUtm };
    dateOp = draftDateOp;
    date1 = draftDate1;
    date2 = draftDate2;
    filterOpen = false;
    load();
  }

  async function backfillContacts(): Promise<void> {
    if (backfilling) return;
    if (!confirm('Підтягнути контакти з усіх оплат? Це створить/злиє контакти за email і телефоном. Виконується у фоні ~хвилину.')) return;
    backfilling = true;
    backfillMsg = null;
    try {
      const r = await api.adminBackfillContacts();
      backfillMsg = r.alreadyRunning
        ? 'Вже виконується…'
        : 'Запущено у фоні. Оновіть Контакти за ~1 хв.';
      // Poll status until it finishes, then clear.
      const poll = setInterval(async () => {
        try {
          const s = await api.adminBackfillContactsStatus();
          if (!s.running) {
            clearInterval(poll);
            backfilling = false;
            backfillMsg = 'Готово — контакти підтягнуто. Перевірте Контакти.';
            await load();
          }
        } catch {
          clearInterval(poll);
          backfilling = false;
        }
      }, 5000);
    } catch {
      backfillMsg = 'Помилка запуску';
      backfilling = false;
    }
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    adminMode.set('admin');
    try {
      products = (await api.adminProducts()).items;
    } catch {
      products = [];
    }
    await load();
  });
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Оплати</div>
    <span class="header-spacer"></span>
  </div>

  <div class="scroll">
    <div class="search-row">
      <input
        class="search"
        type="search"
        placeholder="Пошук: email, телефон, telegram, № замовлення…"
        bind:value={search}
        oninput={onSearchInput}
      />
      <button
        type="button"
        class="filter-btn icon-only"
        class:has-active={activeFiltersCount > 0}
        onclick={openFilterModal}
        aria-label="Відкрити фільтри"
        title="Фільтр"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 14.4V20a1 1 0 0 1-1.5.87l-3-1.7A1 1 0 0 1 9 18.3V14.4L3.2 5.6A1 1 0 0 1 3 5z"/>
        </svg>
        {#if activeFiltersCount > 0}
          <span class="filter-badge">{activeFiltersCount}</span>
        {/if}
      </button>
    </div>

    <div class="backfill-row">
      <button type="button" class="backfill-btn" disabled={backfilling} onclick={backfillContacts}>
        {backfilling ? 'Підтягую контакти…' : 'Підтягнути контакти з оплат'}
      </button>
      {#if backfillMsg}<span class="backfill-msg">{backfillMsg}</span>{/if}
    </div>

    {#if loading && !payments}
      <div class="placeholder">Завантажуємо…</div>
    {:else if error}
      <div class="placeholder err">{error}</div>
    {:else if payments && payments.length === 0}
      <div class="placeholder">Оплат не знайдено</div>
    {:else if payments}
      <div class="count">Знайдено: {total} · Сума: <span class="count-sum">{uah(sumCents)}</span></div>
      {#each payments as p (p.id)}
        <div
          class="pay-row clickable"
          role="button"
          tabindex="0"
          onclick={() => push(`/admin/payment/${p.id}`)}
          onkeydown={(e) => e.key === 'Enter' && push(`/admin/payment/${p.id}`)}
        >
          <div class="pay-main">
            <div class="pay-top">
              <span class="pay-user">{p.userName || p.payerName || (p.telegramId ? `ID ${p.telegramId}` : '—')}</span>
              <span class="pay-amount" class:refund={p.status === 'refunded'}>
                {p.status === 'refunded' ? '−' : ''}{uah(p.amountCents)}
              </span>
            </div>
            <div class="pay-sub">
              {#if p.product}
                <span class="pay-prod">{p.product.title}</span>
              {:else if p.rawProduct}
                <span class="pay-prod muted">{p.rawProduct}</span>
                <span class="unmatched">не зіставлено</span>
              {:else}
                <span class="pay-prod muted">—</span>
              {/if}
              <span class="dot">·</span>
              <span>{formatDateTime(p.occurredAt)}</span>
            </div>
            {#if p.utmSource || p.utmCampaign}
              <div class="pay-utm">
                {#if p.utmSource}<span class="utm-tag">{p.utmSource}</span>{/if}
                {#if p.utmCampaign}<span class="utm-tag">{p.utmCampaign}</span>{/if}
              </div>
            {/if}
          </div>
          <span class="badge {STATUS_CLASS[p.status]}">{STATUS_LABEL[p.status]}</span>
        </div>
      {/each}

      {#if hasMore}
        <button type="button" class="more" disabled={loadingMore} onclick={loadMore}>
          {loadingMore ? 'Завантажую…' : 'Завантажити ще'}
        </button>
      {/if}
    {/if}
  </div>

  {#if filterOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" aria-hidden="true" onclick={closeFilterModal}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Фільтри">
      <div class="modal-head">
        <div class="modal-title">Фільтри</div>
        <button type="button" class="modal-close" onclick={closeFilterModal} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <div class="modal-section-title">Статус оплати</div>
        <div class="type-pills">
          {#each (['succeeded', 'refunded', 'failed', 'pending'] as PaymentStatus[]) as st (st)}
            <button
              type="button"
              class="type-pill"
              class:active={draftStatuses.includes(st)}
              onclick={() => toggleDraftStatus(st)}
            >{STATUS_LABEL[st]}</button>
          {/each}
        </div>

        <div class="modal-section-title">Продукт</div>
        <select class="date-op product-full" bind:value={draftProduct}>
          <option value="">Всі продукти</option>
          <option value="unmatched">Не зіставлені</option>
          {#each products as p (p.id)}
            <option value={p.id}>{p.title}</option>
          {/each}
        </select>

        <div class="modal-section-title">Джерело оплати</div>
        {#if availableSources === null}
          <div class="sub-placeholder">Завантажуємо…</div>
        {:else if availableSources.length === 0 && !sourcesHasNone}
          <div class="sub-placeholder">Ще немає джерел</div>
        {:else}
          <div class="type-pills">
            {#each availableSources as s (s.source)}
              <button
                type="button"
                class="type-pill"
                class:active={draftSources.includes(s.source)}
                onclick={() => toggleDraftSource(s.source)}
              >{s.source} <span class="pill-count">{s.count}</span></button>
            {/each}
            {#if sourcesHasNone}
              <button
                type="button"
                class="type-pill"
                class:active={draftSources.includes(SOURCE_NONE)}
                onclick={() => toggleDraftSource(SOURCE_NONE)}
              >Без джерела</button>
            {/if}
          </div>
        {/if}

        <div class="modal-section-title">UTM-мітки оплати</div>
        <UtmFilter scope="payments" bind:selected={draftUtm} />

        <div class="modal-section-title">Дата й час оплати</div>
        <div class="date-filter">
          <select
            class="date-op"
            value={draftDateOp}
            onchange={(e) => {
              draftDateOp = (e.target as HTMLSelectElement).value as '' | DateOp;
              // Formats differ between «дорівнює» (date) and the rest
              // (datetime) — clear so a stale value can't linger.
              draftDate1 = '';
              draftDate2 = '';
            }}
          >
            <option value="">— будь-яка —</option>
            <option value="before">до</option>
            <option value="after">після</option>
            <option value="on">у день</option>
            <option value="between">між</option>
          </select>
          {#if draftDateOp === 'between'}
            <DatePicker
              mode="range"
              rangeTime
              from={draftDate1}
              to={draftDate2}
              placeholder="Оберіть період"
              applyLabel="Обрати"
              onRange={(f, t) => {
                draftDate1 = f;
                draftDate2 = t;
              }}
            />
          {:else if draftDateOp === 'on'}
            <DatePicker value={draftDate1} onChange={(v) => (draftDate1 = v)} />
          {:else if draftDateOp}
            <DatePicker withTime value={draftDate1} onChange={(v) => (draftDate1 = v)} />
          {/if}
        </div>
      </div>
      <div class="modal-actions">
        <button
          type="button"
          class="modal-btn modal-btn-secondary"
          onclick={resetDraftFilters}
          disabled={!draftHasAny}
        >Скинути</button>
        <button
          type="button"
          class="modal-btn modal-btn-primary"
          onclick={applyFilters}
        >Застосувати</button>
      </div>
    </div>
  {/if}
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px; color: var(--text-light); display: flex; align-items: center; gap: 10px; flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-spacer { width: 40px; }
  .scroll { flex: 1; overflow-y: auto; padding: 16px 16px 100px; }

  .search-row { display: flex; gap: 8px; align-items: stretch; margin-bottom: 14px; }
  .search {
    flex: 1; min-width: 0; border: 1px solid var(--border2); border-radius: 10px; padding: 10px 13px;
    font-size: 13px; color: var(--text); background: var(--bg); font-family: inherit; outline: none;
  }
  .search:focus { border-color: var(--outline); }
  .filter-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid var(--border2); background: var(--bg); color: var(--text);
    padding: 9px 12px; border-radius: 10px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: border-color 0.15s var(--ease), color 0.15s var(--ease);
  }
  .filter-btn.has-active { border-color: var(--pu); color: var(--accent-text); }
  .filter-btn.icon-only { padding: 9px 11px; flex-shrink: 0; }
  .filter-badge {
    background: var(--pu); color: var(--text-light); font-size: 10px; padding: 1px 6px;
    border-radius: 999px; min-width: 16px; text-align: center; line-height: 1.4;
  }
  .backfill-row { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .backfill-btn {
    border: 1px solid var(--pu2); background: var(--bg); color: var(--accent-text);
    padding: 8px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .backfill-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .backfill-msg { font-size: 12px; color: var(--text-muted); }
  .count { font-size: 11px; color: var(--text-muted); margin-bottom: 8px; }
  .count-sum { color: var(--text); font-weight: 700; }

  .pay-row {
    display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: var(--bg2);
    border: 1px solid var(--border); border-radius: 12px; margin-bottom: 8px;
  }
  .pay-row.clickable { cursor: pointer; }
  .pay-main { flex: 1; min-width: 0; }
  .pay-top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
  .pay-user {
    font-size: 13px; font-weight: 600; color: var(--text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .pay-amount { font-size: 14px; font-weight: 700; color: var(--text); flex-shrink: 0; }
  .pay-amount.refund { color: var(--status-inactive); }
  .pay-sub { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 11px; color: var(--text-muted); margin-top: 3px; }
  .pay-prod { font-weight: 600; color: var(--text); }
  .pay-prod.muted { color: var(--text-muted); font-weight: 500; }
  .unmatched {
    font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 6px;
    background: var(--danger-bg); color: var(--status-inactive);
  }
  .dot { opacity: 0.5; }
  .badge { padding: 4px 11px; border-radius: 999px; font-size: 11px; font-weight: 600; flex-shrink: 0; }
  .badge-active { background: var(--green-light, var(--green-light)); color: var(--status-active); }
  .badge-expired { background: var(--danger-bg); color: var(--status-inactive); }
  .badge-muted { background: var(--bg); color: var(--text-muted); }

  .placeholder {
    padding: 30px 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    border: 1px dashed var(--border2); border-radius: 12px; background: var(--bg2);
  }
  .placeholder.err { color: var(--status-inactive); }
  .more {
    width: 100%; margin-top: 10px; padding: 12px; border: 1px solid var(--border2);
    border-radius: 10px; background: var(--bg); color: var(--text); font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .more:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Filter modal (bottom sheet), mirrors the Subscriptions filter ── */
  .modal-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); z-index: 1000; }
  .modal-sheet {
    position: fixed; left: 50%; bottom: 0; transform: translateX(-50%);
    width: min(100%, 480px); max-height: 85vh; background: var(--bg);
    border-radius: 16px 16px 0 0; z-index: 1001; display: flex; flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 18px 12px; border-bottom: 0.5px solid var(--border); flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px; height: 30px; border-radius: 50%; background: var(--bg2); border: none;
    color: var(--text-muted); cursor: pointer; font-size: 14px; font-family: inherit;
    display: flex; align-items: center; justify-content: center;
  }
  .modal-body { padding: 14px 18px 18px; overflow-y: auto; flex: 1; min-height: 0; }
  .modal-section-title {
    font-size: 11px; font-weight: 700; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: 1px; margin: 14px 0 8px;
  }
  .modal-section-title:first-child { margin-top: 0; }
  .pay-utm { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
  .utm-tag {
    font-size: 10px; color: var(--text-muted); background: var(--bg2);
    border: 0.5px solid var(--border2); border-radius: 999px; padding: 1px 7px;
    max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .type-pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 4px; }
  .type-pill {
    border: 1px solid var(--border2); background: var(--bg); color: var(--text);
    padding: 7px 12px; border-radius: 999px; font-size: 12px; font-weight: 500;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s var(--ease), color 0.15s var(--ease), border-color 0.15s var(--ease);
  }
  .type-pill.active { background: var(--pu); color: var(--text-light); border-color: var(--pu); }
  .pill-count { opacity: 0.6; font-size: 11px; }
  .product-full { width: 100%; }
  .date-filter { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 4px; }
  .date-op {
    border: 1px solid var(--border2); border-radius: 8px; padding: 8px 10px;
    font-size: 12px; color: var(--text); background: var(--bg); font-family: inherit;
    cursor: pointer; outline: none;
  }
  .date-op:focus { border-color: var(--outline); }
  .sub-placeholder {
    padding: 14px; text-align: center; color: var(--text-muted); font-size: 12px;
    border: 1px dashed var(--border2); border-radius: 10px; background: var(--bg2); margin-bottom: 4px;
  }
  .modal-actions {
    display: flex; gap: 8px; padding: 12px 18px calc(16px + env(safe-area-inset-bottom));
    border-top: 0.5px solid var(--border); flex-shrink: 0;
  }
  .modal-btn {
    flex: 1; border: none; padding: 12px; border-radius: 10px;
    font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
    transition: opacity 0.15s var(--ease);
  }
  .modal-btn-secondary { background: var(--bg2); color: var(--accent-text); border: 1px solid var(--border2); }
  .modal-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
  .modal-btn-primary { background: linear-gradient(135deg, var(--pu), var(--pu3)); color: var(--text-light); }
</style>
